import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import { FileOutput, AlertCircle, Plus, X, Clock } from 'lucide-react';

// Helper function to format UTC dates
function formatScheduledUTCDate(timestamp) {
  // Create date from UTC timestamp
  const date = new Date(timestamp * 1000);
  
  // Keep the date in UTC when formatting
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC', // Force UTC timezone
    hourCycle: 'h23'  // Use 24-hour format
  }).format(date);
}

// Calculate time until based on UTC time
function formatTimeUntil(timestamp) {
  const now = new Date();
  const utcNow = new Date(now.getTime() + now.getTimezoneOffset() * 60000); // Convert to UTC
  const target = new Date(timestamp * 1000); // Target is already UTC
  
  const diffTime = Math.abs(target - utcNow);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.ceil(diffDays / 7);
  
  if (diffWeeks > 0) {
    return `${diffWeeks} ${diffWeeks === 1 ? __('week') : __('weeks')}`;
  } else if (diffDays > 0) {
    return `${diffDays} ${diffDays === 1 ? __('day') : __('days')}`;
  } else {
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    return `${diffHours} ${diffHours === 1 ? __('hour') : __('hours')}`;
  }
}

const JPKGeneration = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [additionalEmails, setAdditionalEmails] = useState(['']);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [savedEmails, setSavedEmails] = useState([]);
  const [automationEnabled, setAutomationEnabled] = useState(false);
  const [lastGenerated, setLastGenerated] = useState('');
  const [nextScheduled, setNextScheduled] = useState(null);

  // Fetch saved additional emails on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Fetch emails
        const emailsResponse = await fetch(`${boardMemberData.restURL}board-member/v1/jpk-emails`, {
          headers: {
            'X-WP-Nonce': boardMemberData.nonce,
          },
        });
        const emailsData = await emailsResponse.json();
        if (emailsData.success && emailsData.emails) {
          setSavedEmails(emailsData.emails);
          setAdditionalEmails(emailsData.emails.length > 0 ? emailsData.emails : ['']);
        }

        // Fetch automation settings
        const automationResponse = await fetch(`${boardMemberData.restURL}board-member/v1/jpk-automation`, {
          headers: {
            'X-WP-Nonce': boardMemberData.nonce,
          },
        });
        const automationData = await automationResponse.json();
        if (automationData.success) {
          setAutomationEnabled(automationData.automation_enabled);
          setLastGenerated(automationData.last_generated);
          setNextScheduled(automationData.next_scheduled);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, []);
  useEffect(() => {
    const fetchSavedEmails = async () => {
      try {
        const response = await fetch(`${boardMemberData.restURL}board-member/v1/jpk-emails`, {
          headers: {
            'X-WP-Nonce': boardMemberData.nonce,
          },
        });
        const data = await response.json();
        if (data.success && data.emails) {
          setSavedEmails(data.emails);
          setAdditionalEmails(data.emails.length > 0 ? data.emails : ['']);
        }
      } catch (error) {
        console.error('Error fetching saved emails:', error);
      }
    };

    fetchSavedEmails();
  }, []);

  const handleEmailChange = (index, value) => {
    const newEmails = [...additionalEmails];
    newEmails[index] = value;
    setAdditionalEmails(newEmails);
  };

  const addEmailField = () => {
    setAdditionalEmails([...additionalEmails, '']);
  };

  const removeEmailField = (index) => {
    const newEmails = additionalEmails.filter((_, i) => i !== index);
    setAdditionalEmails(newEmails.length > 0 ? newEmails : ['']);
  };

  const saveEmailPreferences = async () => {
    try {
      const validEmails = additionalEmails.filter(email => email.trim() !== '');
      const response = await fetch(`${boardMemberData.restURL}board-member/v1/jpk-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': boardMemberData.nonce,
        },
        body: JSON.stringify({ emails: validEmails }),
      });

      const data = await response.json();
      if (data.success) {
        setMessage('success|Email preferences saved successfully');
        setSavedEmails(validEmails);
      } else {
        setMessage('error|Failed to save email preferences');
      }
    } catch (error) {
      setMessage('error|Error saving email preferences');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const validEmails = additionalEmails.filter(email => email.trim() !== '');
      
      const response = await fetch(`${boardMemberData.restURL}board-member/v1/generate-jpk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': boardMemberData.nonce,
        },
        body: JSON.stringify({
          month,
          year,
          additional_emails: validEmails
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage('success|JPK file generated successfully. You will receive it via email.');
        if (data.download_url) {
          window.open(data.download_url, '_blank');
        }
      } else {
        setMessage('error|' + (data.message || 'Failed to generate JPK file.'));
      }
    } catch (error) {
      setMessage('error|Error generating JPK file. Please try again.');
      console.error('JPK Generation Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAutomation = async () => {
    try {
      const response = await fetch(`${boardMemberData.restURL}board-member/v1/jpk-automation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': boardMemberData.nonce,
        },
        body: JSON.stringify({ enabled: !automationEnabled }),
      });

      const data = await response.json();
      if (data.success) {
        setAutomationEnabled(data.automation_enabled);
        setNextScheduled(data.next_scheduled);
        setMessage('success|Automation settings updated successfully');
      } else {
        setMessage('error|Failed to update automation settings');
      }
    } catch (error) {
      setMessage('error|Error updating automation settings');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <FileOutput className="h-5 w-5 text-gray-500" />
          {__('Generate JPK File')}
        </h3>
      </div>
      
      <div className="p-6 space-y-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Month and Year Selection */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="jpk-month" className="block text-sm font-medium text-gray-700 mb-1">
                {__('Select Month')}
              </label>
              <select
                id="jpk-month"
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const monthNum = i + 1;
                  const monthName = new Date(2024, i, 1).toLocaleString('default', { month: 'long' });
                  return (
                    <option key={monthNum} value={monthNum}>
                      {monthName}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label htmlFor="jpk-year" className="block text-sm font-medium text-gray-700 mb-1">
                {__('Select Year')}
              </label>
              <input
                type="number"
                id="jpk-year"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                min="2020"
                max="2030"
                className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Additional Email Recipients */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700">
                {__('Additional Email Recipients')}
              </label>
              <button
                type="button"
                onClick={addEmailField}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
              >
                <Plus className="h-4 w-4 mr-1" />
                {__('Add Email')}
              </button>
            </div>
            
            {additionalEmails.map((email, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  placeholder={__('Enter email address')}
                  className="block flex-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                {additionalEmails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEmailField(index)}
                    className="inline-flex items-center p-2 border border-transparent rounded-md text-red-700 hover:bg-red-100"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-between gap-4">
            <button
              type="button"
              onClick={saveEmailPreferences}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {__('Save Email Preferences')}
            </button>
            
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
            >
              {loading ? __('Generating...') : __('Generate and Send JPK')}
            </button>
          </div>
        </form>

       {/* Automation Settings Section */}
       <div className="border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-500" />
              {__('Automation Settings')}
            </h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="automation-enabled"
                  type="checkbox"
                  checked={automationEnabled}
                  onChange={toggleAutomation}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="automation-enabled" className="font-medium text-gray-700">
                  {__('Enable Automatic Generation')}
                </label>
                <p className="text-gray-500">
                  {__('Automatically generate and email JPK files at the end of each month')}
                </p>
              </div>
            </div>

            {automationEnabled && nextScheduled && (
  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600 space-y-2">
    <p>
      {__('Next generation scheduled for: ')}
      <span className="font-medium">
        {formatScheduledUTCDate(nextScheduled)}
      </span>
    </p>
    <p>
      {__('Time until next generation: ')}
      <span className="font-medium">
        {formatTimeUntil(nextScheduled)}
      </span>
    </p>
  </div>
)}

{lastGenerated && (
  <div className="text-sm text-gray-600">
    <p>
      {__('Last generated on: ')}
      <span className="font-medium">
        {formatScheduledUTCDate(new Date(lastGenerated).getTime() / 1000)}
      </span>
    </p>
  </div>
)}

            {lastGenerated && (
              <div className="text-sm text-gray-600">
                <p>
                  {__('Last generated on: ')}
                  <span className="font-medium">
                    {new Date(lastGenerated).toLocaleString()}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>

        {message && (
          <div className={`mt-4 p-4 rounded-md flex items-center gap-2 ${
            message.startsWith('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            <AlertCircle className="h-5 w-5" />
            <p>{message.split('|')[1]}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JPKGeneration;